"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export function StickySearchWrapper({ children }: { children: ReactNode }) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const scrollRoot =
      sentinel.closest<HTMLElement>('[data-slot="scroll-area-viewport"]') ??
      undefined;

    if (process.env.NODE_ENV === "development" && !scrollRoot) {
      console.warn(
        "StickySearchWrapper: could not find [data-slot='scroll-area-viewport'] ancestor. Ensure this component is rendered inside a PageContainer.",
      );
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry!.isIntersecting),
      { root: scrollRoot, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} className="h-0 w-full" aria-hidden />
      <div
        className={cn(
          "sticky top-0 z-40 py-2 -mx-4 px-4 transition-[background-color,backdrop-filter] duration-200",
          isStuck
            ? "bg-background/80 backdrop-blur-md"
            : "bg-transparent backdrop-blur-none",
        )}
      >
        <div className="max-w-2xl mx-auto">{children}</div>
      </div>
    </>
  );
}
